<script>
    export default {
        name: "Button",
        props: {
            showHoverEffect: {
                type: Boolean,
                default: false,
            },
            variant: {
                type: String,
                default: "standard",
                validator: function (value) {
                    return (
                        [
                            "standard",
                            "alert",
                            "warning",
                            "contour",
                            "hollow",
                            "link",
                            "link-background-white",
                            "link-without-icon",
                            "link-no-outline",
                            "contour-alert",
                            "tag",
                            "selectable",
                            "grey",
                            "none",
                        ].indexOf(value) !== -1
                    );
                },
            },
            size: {
                type: String,
                default: "primary",
                validator: function (value) {
                    return (
                        [
                            "primary",
                            "secondary",
                            "slim",
                            "small",
                            "icon",
                            "square-icon",
                            "adaptable",
                            "adaptable-input",
                            "none",
                        ].indexOf(value) !== -1
                    );
                },
            },
            /**
             * Bredd på knappen.
             * content - anpassar sin bredd utifrån sitt innehåll
             */
            width: {
                type: String,
                default: "default",
                validator: function (value) {
                    return ["default", "content"].indexOf(value) !== -1;
                },
            },
            isActive: {
                type: Boolean,
                default: false,
            },
            /**
             * Prop som påverkar styling och lägger till laddningsanimation i knappen. Generell lösning
             */
            isLoading: {
                type: Boolean,
                default: false,
            },
            iconPath: {
                type: String,
                default: undefined,
            },
            iconName: {
                type: String,
                default: undefined,
            },
            /**
             * "Knappen" kommer renderas som a element om href är satt och sättas som dess prop.
             * Default är att target är _blank
             */
            href: {
                type: String,
                default: undefined,
            },
            /**
             * Alignment av knappen. Lägger till align-* i css
             */
            align: {
                type: String,
                default: undefined,
                validator: function (value) {
                    return !value || ["start", "center", "end"].indexOf(value) !== -1;
                },
            },
        },
        data() {
            return {
                iconSrc: this.iconPath ? new URL(this.iconPath, import.meta.url).href : undefined,
            };
        },
        computed: {
            klass: function () {
                return [`button-${this.variant}`, `button-${this.size}`];
            },
        },
    };
</script>

<template>
    <component
        :is="href ? 'a' : 'button'"
        class="button"
        :class="[
            klass,
            `width-${width}`,
            align ? `align-${align}` : '',
            { 'show-hover-effect': showHoverEffect, active: isActive, loading: isLoading },
        ]"
        v-bind="$attrs"
        v-on="$listeners"
        :disabled="isLoading ?? $attrs?.disabled"
        :href="href"
        :target="href ? '_blank' : undefined"
    >
        <img v-if="iconPath" :src="iconSrc" :alt="iconName || ''" />
        <slot></slot>
        <!-- Läggs till i knappen om isLoading är satt -->
        <div v-if="isLoading" class="loading-wrapper">
            <img class="loading-icon" src="@/assets/loader_white.svg" />
        </div>
    </component>
</template>

<style lang="scss" scoped>
    @import "@/style/button";

    /*
        isLoading CSS
     */
    .loading {
        position: relative;

        &:disabled {
            // Gör knappen lite mer synlig än normal disabled, för att lättare se laddningen
            opacity: 0.6;
        }

        // Samma som används för laddningsanimation i tabbar
        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }

        .loading-wrapper {
            position: absolute;
            right: 0;

            .loading-icon {
                // Samma som används för laddningsanimation i tabbar
                animation: spin 4s linear infinite;
                height: 1.3rem;
                width: auto;
                max-height: 100%;
                max-width: unset;
                margin-right: 0.5rem;
            }
        }
    }

    .width-content {
        width: max-content;
    }

    .align-start {
        align-self: start;
    }

    .align-center {
        align-self: center;
    }

    .align-end {
        align-self: end;
    }
</style>
